import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Download, FileText, Calendar, User, Phone, Mail, 
  MapPin, CheckCircle, AlertTriangle, Copy, Check, 
  IndianRupee, Landmark, Clock, ArrowRight, ShieldCheck
} from 'lucide-react';
import { guestAPI } from '../utils/api';
import { generatePDF } from '../utils/pdfGenerator';
import { calculateTotalGSTBreakdown, getStateNameFromGST } from '../utils/gstUtils';
import LoadingSpinner from '../components/LoadingSpinner';

const GuestDocumentView = () => {
  const { shareToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [docData, setDocData] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const response = await guestAPI.getDocument(shareToken);
        if (response.data && response.data.success) {
          setDocData(response.data.document);
          setCompanySettings(response.data.companySettings);
        } else {
          setError(response.data?.error || 'Failed to retrieve document details');
        }
      } catch (err) {
        console.error('Error fetching guest document:', err);
        setError(err.response?.data?.error || 'Failed to connect to the server or document has expired.');
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      fetchDocument();
    }
  }, [shareToken]);

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000);
      })
      .catch(err => console.error('Failed to copy text:', err));
  };

  const handlePdfDownload = async () => {
    if (!docData || downloading) return;
    try {
      setDownloading(true);
      // Map properties to match what pdfGenerator expects if needed
      const mappedData = {
        ...docData,
        customer: {
          name: docData.customer_name,
          phone: docData.customer_phone,
          email: docData.customer_email,
          address: docData.customer_address,
          gstNumber: docData.customer_gst
        }
      };

      const docType = docData.documentType || 'estimate';
      await generatePDF(mappedData, docType, companySettings);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4">
        <LoadingSpinner />
        <p className="text-slate-400 mt-4 animate-pulse">Loading secure document portal...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-center">
        <div className="bg-slate-900/60 backdrop-blur-xl border border-red-500/30 p-8 rounded-2xl max-w-md shadow-2xl">
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Denied / Expired</h2>
          <p className="text-slate-400 mb-6 text-sm leading-relaxed">{error}</p>
          <p className="text-xs text-slate-500">Please contact the salesperson who shared this link with you to get an active link.</p>
        </div>
      </div>
    );
  }

  const {
    documentType,
    estimate_number,
    invoice_number,
    closed_order_number,
    date,
    status,
    customer_name,
    customer_phone,
    customer_email,
    customer_address,
    customer_state,
    customer_gst,
    salesperson_name,
    items = [],
    payments = [],
    subtotal,
    total_gst,
    grand_total,
    amountReceived,
    total_outstanding,
    termsAndConditions,
    materialDescription,
    cancellation_status,
    cancellation_reason,
    isWoodWork,
    dispatched_from,
    delivered_to
  } = docData;

  const docNo = invoice_number || closed_order_number || estimate_number || 'DOC-TEMP';
  const displayDocType = documentType === 'invoice' ? 'Tax Invoice' : documentType === 'closed_order' ? 'Closed Order' : (docData.is_proforma || docData.isProforma ? 'Proforma Invoice' : 'Estimate');
  
  // Calculate status details
  const isCancelled = cancellation_status === 'cancelled' || status === 'cancelled';
  const outstandingBalance = grand_total - amountReceived;
  const isFullyPaid = amountReceived >= (grand_total - 0.5);

  let statusBadgeBg = 'bg-amber-500/10 border-amber-500/30 text-amber-400';
  let statusText = 'Pending';

  if (isCancelled) {
    statusBadgeBg = 'bg-red-500/10 border-red-500/30 text-red-400';
    statusText = 'Cancelled';
  } else if (isFullyPaid) {
    statusBadgeBg = 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
    statusText = 'Fully Paid';
  } else if (amountReceived > 0) {
    statusBadgeBg = 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400';
    statusText = 'Partially Paid';
  }

  return (
    <div className="min-h-screen bg-[#070b13] text-slate-100 flex flex-col font-sans antialiased selection:bg-[#c5a57d]/30 selection:text-white pb-12">
      {/* Top Header Glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute top-20 right-1/4 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

      {/* Navigation / Header */}
      <header className="w-full max-w-7xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between border-b border-slate-900/80 z-10 shrink-0 gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-[#c5a57d] rounded-xl flex items-center justify-center overflow-hidden shadow-lg border border-white/10">
            <img 
              src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg" 
              alt="Ideal Trendz Logo"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-white">{companySettings?.companyName || 'IDEAL TRENDZ INTERIORS'}</h1>
            <p className="text-xs text-[#c5a57d] tracking-wide uppercase font-semibold">Premium Design Studio</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full border border-emerald-500/20 flex items-center gap-1.5 font-medium">
            <ShieldCheck className="h-3.5 w-3.5" />
            Verified Secure Link
          </span>
          <button
            onClick={handlePdfDownload}
            disabled={downloading}
            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-sm font-semibold shadow-lg hover:shadow-emerald-500/10 transition-all flex items-center gap-2 border border-emerald-400/20 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            <span>{downloading ? 'Generating...' : 'Download PDF'}</span>
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="w-full max-w-7xl mx-auto px-4 py-8 flex-grow grid grid-cols-1 lg:grid-cols-3 gap-8 z-10">
        
        {/* Left 2 Columns: Main Document Details */}
        <section className="lg:col-span-2 space-y-8">
          
          {/* Hero Section */}
          <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <div>
              <span className="text-xs text-[#c5a57d] font-bold tracking-widest uppercase mb-1 block">{displayDocType}</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">#{docNo}</h2>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-400">
                <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-slate-500" /> {new Date(date).toLocaleDateString('en-GB')}</span>
                <span className="h-3 w-px bg-slate-800"></span>
                <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-slate-500" /> Ref: {salesperson_name}</span>
              </div>
            </div>

            {(!isCancelled && documentType === 'estimate') ? null : (
              <div className="flex flex-col items-start sm:items-end gap-2">
                <span className={`px-4 py-1.5 rounded-xl border text-sm font-bold uppercase tracking-wider ${statusBadgeBg}`}>
                  {statusText}
                </span>
                <p className="text-xs text-slate-500">Last updated: Just now</p>
              </div>
            )}
          </div>

          {/* Cancellation Banner */}
          {isCancelled && (
            <div className="bg-red-500/10 border-2 border-red-500/30 p-6 rounded-3xl flex items-start gap-4">
              <div className="p-3 bg-red-500/20 text-red-400 rounded-2xl mt-0.5">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h4 className="text-red-400 font-bold text-lg">Document Cancelled</h4>
                <p className="text-slate-300 text-sm mt-1 leading-relaxed">
                  Reason: {cancellation_reason || 'No cancellation reason provided.'}
                </p>
              </div>
            </div>
          )}

          {/* Party and Vendor Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* From Company */}
            <div className="bg-slate-900/30 border border-slate-800/80 rounded-3xl p-6 hover:border-slate-700/60 transition-colors">
              <h3 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
                <Landmark className="h-4 w-4 text-[#c5a57d]" /> Issued By
              </h3>
              <div className="space-y-2">
                <h4 className="font-extrabold text-white text-base">{companySettings?.companyName}</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{companySettings?.addressLine1} {companySettings?.addressLine2}</p>
                <p className="text-sm text-slate-400">{companySettings?.city}, {companySettings?.state} - {companySettings?.pincode}</p>
                <div className="pt-2 space-y-1">
                  {companySettings?.gstNumber && <p className="text-xs text-slate-500">GSTIN: <span className="font-mono text-slate-300">{companySettings.gstNumber}</span></p>}
                  {companySettings?.emailId && <p className="text-xs text-slate-500">Email: <span className="text-slate-300">{companySettings.emailId}</span></p>}
                </div>
              </div>
            </div>

            {/* To Customer */}
            <div className="bg-slate-900/30 border border-slate-800/80 rounded-3xl p-6 hover:border-slate-700/60 transition-colors">
              <h3 className="text-[#c5a57d] text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
                <User className="h-4 w-4 text-emerald-400" /> Customer / Client
              </h3>
              <div className="space-y-2">
                <h4 className="font-extrabold text-white text-base">{customer_name}</h4>
                {customer_address && (
                  <p className="text-sm text-slate-400 leading-relaxed">{customer_address}</p>
                )}
                <div className="pt-2 space-y-1">
                  {customer_phone && <p className="text-xs text-slate-500 flex items-center gap-1.5">Phone: <span className="text-slate-300 font-medium">{customer_phone}</span></p>}
                  {customer_email && <p className="text-xs text-slate-500 flex items-center gap-1.5">Email: <span className="text-slate-300 font-medium">{customer_email}</span></p>}
                  {customer_gst && <p className="text-xs text-slate-500 flex items-center gap-1.5">GSTIN: <span className="text-slate-300 font-mono">{customer_gst}</span></p>}
                  {dispatched_from && <p className="text-xs text-[#c5a57d] flex items-center gap-1.5 pt-1.5 border-t border-slate-800/40">Dispatched From: <span className="text-slate-300 font-semibold">{dispatched_from}</span></p>}
                  {delivered_to && <p className="text-xs text-[#c5a57d] flex items-center gap-1.5">Delivered To: <span className="text-slate-300 font-semibold">{delivered_to}</span></p>}
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-slate-900/30 border border-slate-800/80 rounded-3xl overflow-hidden shadow-lg">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-900/10">
              <h3 className="font-extrabold text-white text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-400" /> Product Details
              </h3>
              {isWoodWork && (
                <span className="text-[10px] bg-sky-500/10 border border-sky-500/20 text-sky-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                  Wood Work
                </span>
              )}
            </div>

            <div className="w-full overflow-x-hidden">
              <table className="w-full text-sm text-left">
                {isWoodWork ? (
                  /* 🪵 Wood Work Details Grid */
                  <>
                    <thead>
                      <tr className="bg-slate-950/40 text-slate-400 text-xs font-bold uppercase border-b border-slate-850">
                        <th className="px-2 py-3 text-center w-10">S.No</th>
                        <th className="px-3 py-3 text-left">Item Details / Category</th>
                        <th className="px-2 py-3 text-center">Nos</th>
                        <th className="px-2 py-3 text-center">L (ft)</th>
                        <th className="px-2 py-3 text-center">B (ft)</th>
                        <th className="px-2 py-3 text-center">Qty (sft)</th>
                        <th className="px-2 py-3 text-center">Rate</th>
                        <th className="px-2 py-3 text-center">GST %</th>
                        <th className="px-3 py-3 text-right pr-4">Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {(() => {
                        const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                        const rows = [];
                        const uniqueCats = [];
                        
                        items.forEach(item => {
                          const cat = item.categoryLabel || '';
                          if (!uniqueCats.includes(cat)) uniqueCats.push(cat);
                        });

                        uniqueCats.forEach((catLabel) => {
                          const catIdx = uniqueCats.indexOf(catLabel);
                          const catEntries = items.filter(item => (item.categoryLabel || '') === catLabel);

                          // Category header row
                          rows.push(
                            <tr key={`cat-row-${catLabel}`} className="bg-slate-900/40 font-bold border-b border-slate-800/85">
                              <td className="p-3 text-center text-[#c5a57d] font-bold">
                                {CAT_LETTERS[catIdx] || (catIdx + 1)}
                              </td>
                              <td colSpan="8" className="p-3 text-[#c5a57d] uppercase tracking-wider text-xs font-black">
                                {catLabel || 'General Items'}
                              </td>
                            </tr>
                          );

                          // Individual item rows
                          catEntries.forEach((item, siNo) => {
                            const nos = item.nos || 0;
                            const length = item.length || 0;
                            const breadth = item.breadth || 0;
                            const qty = item.quantity || (nos * length * breadth);
                            const rate = item.unit_price || 0;
                            const gstRate = item.gst_rate || 0;
                            const totalVal = item.total_amount || (qty * rate * (1 + gstRate / 100));

                            rows.push(
                              <tr key={`item-${siNo}-${catLabel}`} className="hover:bg-slate-800/20 transition-colors">
                                <td className="px-2 py-3 text-center text-slate-500">{siNo + 1}</td>
                                <td className="px-3 py-3 text-center">
                                  <div className="font-semibold text-white whitespace-normal break-words">{item.product_name}</div>
                                </td>
                                <td className="px-2 py-3 text-center font-semibold text-slate-300">{nos}</td>
                                <td className="px-2 py-3 text-center text-slate-300">{length}</td>
                                <td className="px-2 py-3 text-center text-slate-300">{breadth}</td>
                                <td className="px-2 py-3 text-center font-bold text-slate-200 bg-slate-900/10">{qty.toFixed(2)}</td>
                                <td className="px-2 py-3 text-center font-medium text-slate-300">₹{(rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                                <td className="px-2 py-3 text-center text-slate-400">{gstRate}%</td>
                                <td className="px-3 py-3 text-right font-bold text-white pr-4">
                                  ₹{(totalVal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            );
                          });
                        });
                        return rows;
                      })()}
                    </tbody>
                  </>
                ) : (
                  /* 📦 Standard Product Items */
                  <>
                    <thead>
                      <tr className="bg-slate-950/40 text-slate-400 text-xs font-bold uppercase border-b border-slate-850">
                        <th className="px-2 py-3 text-center w-10">S.No</th>
                        <th className="px-3 py-3 text-center">Product Description</th>
                        <th className="px-2 py-3 text-center">HSN Code</th>
                        <th className="px-2 py-3 text-center">QTY</th>
                        <th className="px-2 py-3 text-center">Rate</th>
                        <th className="px-2 py-3 text-center">Unit</th>
                        {items.some(i => (i.discount || 0) > 0) && <th className="px-2 py-3 text-center">Disc %</th>}
                        <th className="px-2 py-3 text-center">Amount</th>
                        <th className="px-2 py-3 text-center">GST %</th>
                        <th className="px-2 py-3 text-center">GST Amount</th>
                        <th className="px-3 py-3 text-center">Total Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {items.map((item, idx) => {
                        const nos = item.quantity || 0;
                        const price = item.unit_price || 0;
                        const discount = item.discount || 0;
                        const taxableAmount = item.amount || (nos * price * (1 - discount / 100));
                        const gstRate = item.gst_rate || 0;
                        const gstVal = item.gst_amount || (taxableAmount * gstRate / 100);
                        const totalVal = item.total_amount || (taxableAmount + gstVal);

                        return (
                          <tr key={idx} className="hover:bg-slate-800/20 transition-colors">
                            <td className="px-2 py-3 text-center text-slate-500">{idx + 1}</td>
                            <td className="px-3 py-3 font-semibold text-white text-center whitespace-normal break-words">{item.product_name}</td>
                            <td className="px-2 py-3 text-center text-slate-400 font-mono">{item.hsn_code || '-'}</td>
                            <td className="px-2 py-3 text-center font-bold text-slate-300">{nos}</td>
                            <td className="px-2 py-3 text-center font-medium text-slate-300">₹{(price || 0).toLocaleString('en-IN')}</td>
                            <td className="px-2 py-3 text-center text-slate-400">{item.unit_type || 'PCS'}</td>
                            {items.some(i => (i.discount || 0) > 0) && (
                              <td className="px-2 py-3 text-center font-semibold text-emerald-400">{discount}%</td>
                            )}
                            <td className="px-2 py-3 text-center font-medium text-slate-300">₹{(taxableAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                            <td className="px-2 py-3 text-center text-slate-400">{gstRate}%</td>
                            <td className="px-2 py-3 text-center font-medium text-slate-300">₹{(gstVal || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                            <td className="px-3 py-3 text-center font-bold text-slate-200">
                              ₹{(totalVal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </>
                )}
              </table>
            </div>
          </div>

          {/* Terms and Conditions / Material Specifications */}
          <div className="grid grid-cols-1 gap-6">
            {termsAndConditions && (
              <div className="bg-slate-900/20 border border-slate-800/60 rounded-3xl p-6">
                <h4 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-3">Terms & Conditions</h4>
                <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap italic">{termsAndConditions}</p>
              </div>
            )}

            {materialDescription && (
              <div className="bg-slate-900/20 border border-slate-800/60 rounded-3xl p-6">
                <h4 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-3">Material Specifications</h4>
                <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap italic">{materialDescription}</p>
              </div>
            )}
          </div>
        </section>

        {/* Right Column: Financial Summary, Payment Timeline, Bank Transfers */}
        <section className="space-y-8">
          
          {/* Financial Breakdown Card */}
          <div className="bg-gradient-to-br from-slate-900 to-[#0e1726] border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl"></div>
            
            <h3 className="text-white font-extrabold text-lg mb-6 flex items-center gap-2 border-b border-slate-800 pb-3">
              <IndianRupee className="h-5 w-5 text-emerald-400" /> Order Summary
            </h3>

            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm text-slate-400">
                <span>Subtotal (Excl. Tax)</span>
                <span className="font-semibold text-slate-200">₹{(subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</span>
              </div>
              <div className="flex justify-between items-center text-sm text-slate-400">
                <span>Total GST Tax</span>
                <span className="font-semibold text-slate-200">₹{(total_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</span>
              </div>

              {/* Dynamic GST breakdown list */}
              {(() => {
                const breakdown = calculateTotalGSTBreakdown(items, customer_gst || '', companySettings);
                if (breakdown.type === 'CGST_SGST') {
                  return (
                    <div className="bg-slate-950/40 p-3 rounded-2xl border border-slate-800/60 text-xs space-y-1.5 font-mono text-slate-500">
                      <div className="flex justify-between">
                        <span>SGST portion ({breakdown.sgstPercentage || 0}%)</span>
                        <span>₹{(breakdown.sgstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>CGST portion ({breakdown.cgstPercentage || 0}%)</span>
                        <span>₹{(breakdown.cgstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="bg-slate-950/40 p-3 rounded-2xl border border-slate-800/60 text-xs space-y-1.5 font-mono text-slate-500">
                      <div className="flex justify-between">
                        <span>IGST portion ({breakdown.igstPercentage || 0}%)</span>
                        <span>₹{(breakdown.igstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  );
                }
              })()}

              <div className="border-t border-slate-800 pt-4 flex justify-between items-center">
                <span className="text-slate-300 font-bold">Grand Total</span>
                <span className="text-white text-2xl font-black">₹{(grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</span>
              </div>

              {documentType !== 'estimate' && (
                <div className="border-t border-slate-800/60 pt-4 space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Total Paid</span>
                    <span className="text-emerald-400 font-extrabold text-base">₹{(amountReceived || 0).toLocaleString('en-IN')}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Outstanding Balance</span>
                    <span className={`font-black text-lg ${outstandingBalance > 0.5 ? 'text-red-400' : 'text-emerald-400'}`}>
                      ₹{(outstandingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bank Details / Transfer Card (Show prominently if there's outstanding balance) */}
          {(documentType === 'estimate' || outstandingBalance > 0.5) && companySettings?.bankName && companySettings.bankName !== 'Not Configured' && (
            <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 shadow-xl relative">
              <div className="absolute top-3 right-4">
                <span className="text-[10px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span> Active Transfer
                </span>
              </div>

              <h3 className="text-white font-extrabold text-base mb-4 flex items-center gap-2">
                <Landmark className="h-5 w-5 text-[#c5a57d]" /> Bank Account Details
              </h3>
              <p className="text-slate-400 text-xs mb-4 leading-relaxed">
                Please transfer the outstanding balance via wire transfer / NetBanking using the details below:
              </p>

              <div className="space-y-3 bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80 font-mono text-sm relative">
                {/* Account Name */}
                <div className="flex justify-between items-start gap-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">A/C Holder Name</span>
                    <span className="text-slate-300 font-semibold text-xs break-all">{companySettings.accountHolderName || companySettings.companyName}</span>
                  </div>
                  <button 
                    onClick={() => handleCopy(companySettings.accountHolderName || companySettings.companyName, 'holder')}
                    className="p-1.5 bg-slate-900 text-slate-400 rounded-lg hover:text-white transition-colors"
                  >
                    {copiedField === 'holder' ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>

                {/* Account Number */}
                <div className="flex justify-between items-start gap-4 pt-2 border-t border-slate-900">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">Account Number</span>
                    <span className="text-white font-black text-sm tracking-wider">{companySettings.accountNumber}</span>
                  </div>
                  <button 
                    onClick={() => handleCopy(companySettings.accountNumber, 'acNo')}
                    className="p-1.5 bg-slate-900 text-slate-400 rounded-lg hover:text-white transition-colors"
                  >
                    {copiedField === 'acNo' ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>

                {/* IFSC Code */}
                <div className="flex justify-between items-start gap-4 pt-2 border-t border-slate-900">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">IFSC / RTGS Code</span>
                    <span className="text-white font-black tracking-widest text-sm">{companySettings.ifscCode}</span>
                  </div>
                  <button 
                    onClick={() => handleCopy(companySettings.ifscCode, 'ifsc')}
                    className="p-1.5 bg-slate-900 text-slate-400 rounded-lg hover:text-white transition-colors"
                  >
                    {copiedField === 'ifsc' ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>

                {/* Bank details & Branch */}
                <div className="pt-2 border-t border-slate-900 flex flex-col text-[11px] text-slate-500">
                  <span>Bank: <strong className="text-slate-400">{companySettings.bankName}</strong></span>
                  {companySettings.branchName && <span>Branch: <strong className="text-slate-400">{companySettings.branchName}</strong></span>}
                </div>
              </div>
            </div>
          )}

          {/* Payment Timeline / Receipt History */}
          {documentType !== 'estimate' && (
            <div className="bg-slate-900/30 border border-slate-800/80 rounded-3xl p-6">
              <h3 className="text-white font-extrabold text-base mb-6 flex items-center gap-2">
                <Clock className="h-5 w-5 text-indigo-400" /> Payment History
              </h3>

              {payments.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-sm bg-slate-950/20 rounded-2xl border border-slate-900">
                  No payments received yet
                </div>
              ) : (
                <div className="relative border-l border-slate-800 ml-3 space-y-6">
                  {payments.map((p, pIdx) => (
                    <div key={pIdx} className="relative pl-6">
                      {/* Circle icon marker */}
                      <span className="absolute -left-[9px] top-1.5 w-4.5 h-4.5 rounded-full bg-slate-950 border border-emerald-500/50 flex items-center justify-center">
                        <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                      </span>

                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <div className="font-bold text-slate-200 text-sm">₹{(p.amount || 0).toLocaleString('en-IN')}</div>
                          <p className="text-xs text-slate-500 mt-0.5">{new Date(p.date || Date.now()).toLocaleDateString('en-GB')} • {p.paymentType || 'Cash'}</p>
                          {p.notes && <p className="text-xs text-slate-400 italic mt-1.5 bg-slate-950/30 px-2.5 py-1 rounded-lg border border-slate-900 w-fit">{p.notes}</p>}
                        </div>
                        
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/20 font-bold uppercase tracking-wider">
                          Cleared
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {/* Footer copyright */}
      <footer className="w-full text-center py-8 text-xs text-slate-600 mt-12 border-t border-slate-950">
        <p>&copy; {new Date().getFullYear()} {companySettings?.companyName || 'Ideal Trendz Interiors'}. All rights reserved.</p>
        <p className="mt-1 text-[10px] text-slate-700">Security protocol: AES-256 encrypted public view token.</p>
      </footer>
    </div>
  );
};

export default GuestDocumentView;
